import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import ContentStudioMovementsStore from "stores/ContentStudioMovementsStore";

import FormControl, {
  TextFieldControl,
  UploadPreviewFieldControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";

import {
  compose,
  lengthValidator,
  imageTypesValidator,
  fileSizeValidator,
  imageIsCorruptedValidator,
} from "utils/validators";

import { ACCEPTED_IMAGE_TYPES } from "constants/image";

import { Container, Buttons, ButtonContainer } from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class MovementForm extends React.Component<Props> {
  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Movement" : "Edit Movement";
  }

  @computed get initialValues() {
    return this.isEditMode ? ContentStudioMovementsStore.details?.formData : {};
  }

  componentDidMount() {
    const { initialValues } = this.props;

    if (initialValues?.id) {
      ContentStudioMovementsStore.fetchMovementDetails(initialValues.id);
    }
  }

  componentWillUnmount() {
    ContentStudioMovementsStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  render() {
    const { onClose, onSubmit, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={onSubmit} initialValues={this.initialValues}>
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <Row>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
          </Row>

          {!isFormLoading ? (
            <>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Title"
                  name="title"
                  placeholder="Enter title"
                  validate={lengthValidator(100)}
                  fullWidth
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  name="description"
                  variant="outlined"
                  margin="none"
                  label="Description"
                  placeholder="Enter description"
                  rows={4}
                  rowsMax={4}
                  validate={lengthValidator(2000)}
                  fullWidth
                  multiline
                  required
                />
              </Row>

              <Row>
                <UploadPreviewFieldControl
                  name="file"
                  title="Image"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                    imageIsCorruptedValidator,
                  )}
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="videoUrl"
                  label="URL"
                  placeholder="Enter URL"
                  validate={lengthValidator(1000)}
                  fullWidth
                  required
                />
              </Row>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default MovementForm;
